/**
 * Shared noise filters for captured console/network diagnostics.
 *
 * @module types/diagnostics-filters
 * @description The same "what counts as noise" decision has to be made by every
 * producer of diagnostics: the Playwright trace extractor on the host, and the
 * in-container collector that records a customer's Selenium suite. Two copies
 * would mean a bug fixed in one surface and not the other, and — worse — the
 * same failing run rendering differently depending on which lane recorded it.
 *
 * Lives in `@chatsdet/types` because it is the only package both sides already
 * depend on; the values are plain data (regexes and a byte cap), not behaviour.
 */

export interface DiagnosticsFilters {
  /** URL patterns to exclude from network requests. */
  excludeUrlPatterns?: RegExp[];
  /** Console message patterns to exclude. */
  excludeConsolePatterns?: RegExp[];
  /** Maximum body size in bytes for network requests (truncate beyond). */
  maxBodySize?: number;
}

/**
 * Default filters for excluding development noise, static assets and SSR data.
 *
 * The bar is "would a human triaging a failed test ever read this line?" — HMR
 * chatter, favicon fetches and font requests never clear it, and leaving them in
 * buries the request that actually 500'd.
 */
export const DEFAULT_DIAGNOSTICS_FILTERS: DiagnosticsFilters = {
  excludeUrlPatterns: [
    /_next\/data\//, // Next.js SSR data
    /_next\/static\//, // Next.js static assets
    /_next\/image/, // Next.js image optimization
    /\.hot-update\./, // HMR updates
    /webpack-hmr/, // Webpack HMR
    /\/__webpack_hmr/, // Webpack HMR
    /favicon\.ico/, // Favicon
    /\.(png|jpg|jpeg|gif|svg|ico|webp|woff2?|ttf|eot|otf)(\?|$)/i, // Static assets
    /sockjs-node/, // Dev server socket
    /@vite\/client/, // Vite client
    /__vite_ping/, // Vite ping
    /chrome-extension:\/\//, // Browser extensions
  ],
  excludeConsolePatterns: [
    /^\[HMR\]/,
    /^\[Fast Refresh\]/,
    /Download the React DevTools/,
    /^\[vite\]/,
    /^%c/, // Styled console logs (often debug output)
  ],
  /** 50KB — enough for a JSON error payload, not enough to bloat an artifact. */
  maxBodySize: 50 * 1024,
};

/** True when a network URL should be dropped as noise. */
export function isNoisyUrl(url: string, filters: DiagnosticsFilters): boolean {
  return filters.excludeUrlPatterns?.some((pattern) => pattern.test(url)) ?? false;
}

/** True when a console message should be dropped as noise. */
export function isNoisyConsoleText(text: string, filters: DiagnosticsFilters): boolean {
  return filters.excludeConsolePatterns?.some((pattern) => pattern.test(text)) ?? false;
}
